import { inject, Injectable } from '@angular/core';
import { Amplify, Hub } from '@aws-amplify/core';
import { MessageAndTopic } from '@cco/model';
import { Store } from '@ngrx/store';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { CONNECTION_STATE_CHANGE, ConnectionState, PubSub } from '@aws-amplify/pubsub';
import { notNullish } from '@app/shared/rxjs/operators/standard-operators';
import { AmplifyCredentialsProviderService } from '@core/services/amplify-credentials-provider.service';
import { selectPubSubOptions } from '@core/iot-data/iot-data.reducer';
import { amplifyServiceActions } from '@core/iot-data/iot-data.actions';
import * as Sentry from '@sentry/angular';

type PubSubContent = Record<string | symbol, unknown>;

@Injectable({
  providedIn: 'root',
})
export class AmplifyService {
  private readonly store = inject(Store);
  private readonly pubSubOptions$ = this.store.select(selectPubSubOptions);

  private readonly amplifyCredentialsProvider = inject(AmplifyCredentialsProviderService);

  private initStarted = false;
  private readonly initCompleteSource$ = new BehaviorSubject<boolean>(false);
  private readonly initComplete$ = this.initCompleteSource$.pipe(filter(initComplete => !!initComplete));

  private pubSub: PubSub;

  constructor() {
    Amplify.configure({}, {
      Auth: {
        credentialsProvider: this.amplifyCredentialsProvider,
      },
    });
  }

  private init$(): Observable<boolean> {
    if (this.initStarted)
      return this.initComplete$;

    this.initStarted = true;

    Hub.listen('pubsub', (data: any) => {
      if (data.payload.event === CONNECTION_STATE_CHANGE) {
        const connectionState = data.payload.data.connectionState as ConnectionState;
        this.store.dispatch(amplifyServiceActions.pubSubConnectionStateUpdate({
          connectionState,
        }));
      }
    });

    this.pubSubOptions$.pipe(
      notNullish()
    ).subscribe((pubSubOptions) => {
      if (this.pubSub) {
        Sentry.addBreadcrumb({
          type: 'iot-data',
          category: 'pubsub.config',
          message: 'PubSub options have changed and a new connection is needed.  This has not been well tested.',
          level: 'info',
        });
        this.pubSub.configure(pubSubOptions);
      } else {
        this.pubSub = new PubSub(pubSubOptions);
      }

      this.subscribe$(`$aws/events/subscriptions/+/${pubSubOptions.clientId}`)
        .subscribe({
          error: err => console.warn('An error occurred in the base IoT client subscription.', err),
          complete: () => console.warn('The base IoT client subscription completed.'),
        });

      this.initCompleteSource$.next(true);
    });

    return this.initComplete$;
  }

  public publish$(topics: string | string[], payload: unknown): Observable<void> {
    return this.init$().pipe(mergeMap(() => from(this.pubSub.publish({ topics, message: payload as PubSubContent }))));
  }

  public publish(topics: string | string[], payload: unknown) {
    this.publish$(topics, payload).pipe(take(1)).subscribe();
  }

  public subscribe$<T>(topicFilters: string | string[]): Observable<MessageAndTopic<T>> {
    return this.init$().pipe(
      mergeMap(() => {
        return this.pubSub.subscribe({
          topics: topicFilters,
        });
      }),
      map((data) => {
        const topicSymbol = Object.getOwnPropertySymbols(data)
          .find(sym => 'Symbol(topic)' === sym.toString());
        const { [topicSymbol]: topic, ...message } = data;
        return {
          topic: topic as string,
          message: message as T,
        };
      }),
    );
  }
}
